import React, { useState } from "react";
import {
  Box,
  Card,
  CardBody,
  Text,
  Button,
  VStack,
  HStack,
  Progress,
  SimpleGrid,
  useColorModeValue,
  Icon,
  Container,
  Heading,
  Flex,
  IconButton,
  useBreakpointValue,
} from "@chakra-ui/react";
import { motion, AnimatePresence, PanInfo } from "framer-motion";
import {
  FaGamepad,
  FaCheck,
  FaStar,
  FaFire,
  FaTrophy,
  FaRocket,
} from "react-icons/fa";
import {
  FiSend,
  FiTarget,
  FiUsers,
  FiPlay,
  FiCpu,
  FiBookOpen,
  FiTrendingUp,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { colors } from "../../assets/styles/colors";

// This component now renders realistic page previews that look exactly like the real pages

// This component now renders actual components from real pages

interface FeatureTourProps {
  onNext: (data?: any) => void;
  onPrevious: () => void;
  stepData: Record<string, any>;
  isLoading: boolean;
}

const MotionBox = motion(Box);

const FeatureTour: React.FC<FeatureTourProps> = ({ onNext, isLoading, stepData }) => {
  const { t } = useTranslation();

  // Theme colors
  const bgGradient = useColorModeValue(
    "linear(to-br, gray.50, blue.50, purple.50)",
    "linear(to-br, gray.900, blue.900, purple.900)"
  );
  const overlayBg = useColorModeValue(
    "rgba(255, 255, 255, 0.95)",
    "rgba(26, 32, 44, 0.95)"
  );
  const cardBg = useColorModeValue("white", colors.primary[700]);
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.600", "gray.300");
  const accentColor = useColorModeValue("blue.500", "blue.400");
  const successColor = useColorModeValue("green.500", "green.400");
  const cardBorder = useColorModeValue("gray.200", "gray.700");
  const previewBg = useColorModeValue("white", colors.primary[800]);
  const progressTrackBg = useColorModeValue("gray.200", "gray.600");
  const dotInactiveBg = useColorModeValue("gray.300", "gray.600");

  // Responsive values
  const containerHeight = useBreakpointValue({ base: "100vh", md: "90vh" });
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [currentFeature, setCurrentFeature] = useState(0);
  const [completedFeatures, setCompletedFeatures] = useState<number[]>([]);
  const [showWelcomeSplash, setShowWelcomeSplash] = useState(false);

  // Get the user's native language for context
  const selectedWords = stepData?.selectedVocabulary || [];

  // Function to render realistic page previews that look exactly like the real pages
  function renderPagePreview(pageType: string) {
    switch (pageType) {
      case "play":
        return (
          <Box className="css-16ktbll" position="relative" opacity={0.4} p={4}>
            <Container maxW="container.md">
              <Card
                bg={previewBg}
                borderRadius="xl"
                boxShadow="xl"
                overflow="hidden"
                className="chakra-card"
                border="1px solid"
                borderColor={cardBorder}
                minH="500px"
              >
                {/* Purple progress bar at top */}
                <Box bg="purple.500" h="6px" w="full" />
                <CardBody p={8}>
                  <VStack spacing={6}>
                    {/* Header with stats */}
                    <Flex
                      justify="space-between"
                      width="full"
                      alignItems="center"
                    >
                      <VStack spacing={1} align="start">
                        <Heading size="lg" color={textColor}>
                          🎮 Play Mode
                        </Heading>
                        <Text fontSize="sm" color={mutedTextColor}>
                          Level 3 • Intermediate
                        </Text>
                      </VStack>
                      <VStack spacing={2} align="end">
                        <HStack spacing={4}>
                          <HStack>
                            <Text fontSize="lg">🧠</Text>
                            <Text fontSize="sm" fontWeight="bold">
                              1,247
                            </Text>
                          </HStack>
                          <HStack>
                            <Text fontSize="lg">🔥</Text>
                            <Text fontSize="sm" fontWeight="bold">
                              7 days
                            </Text>
                          </HStack>
                        </HStack>
                        <Box w="150px" h="2" bg="gray.200" borderRadius="full">
                          <Box
                            w="68%"
                            h="2"
                            bg="purple.500"
                            borderRadius="full"
                          />
                        </Box>
                        <Text fontSize="xs" color={mutedTextColor}>
                          68% today's goal
                        </Text>
                      </VStack>
                    </Flex>

                    {/* Main sentence area */}
                    <Card
                      bg={cardBg}
                      p={6}
                      w="full"
                      borderRadius="xl"
                      boxShadow="md"
                    >
                      <VStack spacing={4}>
                        <Text
                          fontSize="xl"
                          fontWeight="bold"
                          textAlign="center"
                          color={textColor}
                        >
                          Ik ga naar de _____ om boodschappen te doen.
                        </Text>
                        <Text
                          fontSize="md"
                          color={mutedTextColor}
                          textAlign="center"
                        >
                          I am going to the _____ to do shopping.
                        </Text>

                        {/* Answer options */}
                        <VStack spacing={3} w="full" mt={4}>
                          {["winkel", "school", "park", "restaurant"].map(
                            (option, index) => (
                              <Button
                                key={option}
                                variant={index === 0 ? "solid" : "outline"}
                                colorScheme={index === 0 ? "green" : "gray"}
                                size="lg"
                                w="full"
                                borderRadius="xl"
                                fontSize="lg"
                                py={6}
                              >
                                {option}
                              </Button>
                            )
                          )}
                        </VStack>
                      </VStack>
                    </Card>

                    {/* Control buttons */}
                    <HStack spacing={4} flexWrap="wrap" justify="center">
                      <Button
                        colorScheme="green"
                        borderRadius="full"
                        leftIcon={<Text>🧠</Text>}
                      >
                        Remember
                      </Button>
                      <Button
                        colorScheme="red"
                        borderRadius="full"
                        leftIcon={<Text>🤷‍♂️</Text>}
                      >
                        Don't Know
                      </Button>
                      <Button
                        variant="ghost"
                        borderRadius="full"
                        leftIcon={<Text>🔊</Text>}
                      >
                        Listen
                      </Button>
                      <Button
                        variant="ghost"
                        borderRadius="full"
                        leftIcon={<Text>🌍</Text>}
                      >
                        Translate
                      </Button>
                    </HStack>

                    {/* Demo button */}
                    <Button
                      colorScheme="purple"
                      size="lg"
                      leftIcon={<FiPlay />}
                      onClick={() => {
                        // Open dashboard in new tab without affecting current onboarding
                        window.open("/dashboard", "_blank");
                      }}
                    >
                      Try Dashboard Demo
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            </Container>
          </Box>
        );

      case "smart-practice":
        return (
          <Box className="css-16ktbll" position="relative" opacity={0.4} p={4}>
            <Container maxW="container.lg" className="css-v4s7lz">
              <Card
                bg={previewBg}
                borderRadius="xl"
                boxShadow="xl"
                minH="500px"
              >
                <CardBody p={6}>
                  <VStack spacing={4} h="full">
                    {/* Header */}
                    <Flex justify="space-between" w="full" align="center">
                      <Heading size="lg" color={textColor}>
                        💬 Smart Practice
                      </Heading>
                      <Text fontSize="sm" color={mutedTextColor}>
                        AI Chat • Dutch
                      </Text>
                    </Flex>

                    {/* Chat messages */}
                    <VStack
                      spacing={3}
                      flex={1}
                      w="full"
                      align="stretch"
                      overflowY="auto"
                    >
                      <Box alignSelf="flex-start" maxW="70%">
                        <Card bg="blue.50" p={3} borderRadius="xl">
                          <Text>
                            Hallo! Laten we oefenen met Nederlands. Hoe gaat het
                            met je?
                          </Text>
                        </Card>
                      </Box>
                      <Box alignSelf="flex-end" maxW="70%">
                        <Card bg="purple.50" p={3} borderRadius="xl">
                          <Text>
                            Het gaat goed, dank je! Ik leer Nederlands.
                          </Text>
                        </Card>
                      </Box>
                      <Box alignSelf="flex-start" maxW="70%">
                        <Card bg="blue.50" p={3} borderRadius="xl">
                          <Text>
                            Geweldig! Wat vind je het moeilijkste aan
                            Nederlands?
                          </Text>
                        </Card>
                      </Box>
                    </VStack>

                    {/* Input area */}
                    <HStack w="full" spacing={2}>
                      <Box
                        flex={1}
                        bg={cardBg}
                        borderRadius="xl"
                        p={3}
                        border="1px solid"
                        borderColor={cardBorder}
                      >
                        <Text color={mutedTextColor}>
                          Type your message in Dutch...
                        </Text>
                      </Box>
                      <Button colorScheme="blue" borderRadius="xl" px={6}>
                        <Text fontSize="lg">📤</Text>
                      </Button>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </Container>
          </Box>
        );

      case "smart-read":
        return (
          <Box className="css-16ktbll" position="relative" opacity={0.4} p={4}>
            <Container maxW="container.lg" className="chakra-container">
              <Card
                bg={previewBg}
                borderRadius="xl"
                boxShadow="xl"
                minH="500px"
              >
                <CardBody p={6}>
                  <VStack spacing={4}>
                    {/* Header */}
                    <Flex justify="space-between" w="full" align="center">
                      <Heading size="lg" color={textColor}>
                        📖 Smart Read
                      </Heading>
                      <HStack>
                        <Text fontSize="sm" color={mutedTextColor}>
                          Progress: 45%
                        </Text>
                        <Box w="100px" h="2" bg="gray.200" borderRadius="full">
                          <Box
                            w="45%"
                            h="2"
                            bg="teal.500"
                            borderRadius="full"
                          />
                        </Box>
                      </HStack>
                    </Flex>

                    {/* Article content */}
                    <Card bg={cardBg} p={6} w="full" borderRadius="xl">
                      <VStack spacing={4} align="start">
                        <Heading size="md" color={textColor}>
                          De Nederlandse Cultuur
                        </Heading>
                        <Text color={textColor} lineHeight="1.8">
                          Nederland is een klein land in West-Europa. Het is
                          bekend om zijn{" "}
                          <Text
                            as="span"
                            bg="yellow.200"
                            px={1}
                            borderRadius="md"
                            cursor="pointer"
                          >
                            tulpen
                          </Text>
                          ,{" "}
                          <Text
                            as="span"
                            bg="yellow.200"
                            px={1}
                            borderRadius="md"
                            cursor="pointer"
                          >
                            windmolens
                          </Text>{" "}
                          en{" "}
                          <Text
                            as="span"
                            bg="yellow.200"
                            px={1}
                            borderRadius="md"
                            cursor="pointer"
                          >
                            kanalen
                          </Text>
                          . De hoofdstad is Amsterdam, maar de regering zit in
                          Den Haag.
                        </Text>
                        <Text
                          color={mutedTextColor}
                          fontSize="sm"
                          fontStyle="italic"
                        >
                          Click on highlighted words for translations and
                          explanations.
                        </Text>
                      </VStack>
                    </Card>

                    {/* Reading controls - removed as requested */}
                  </VStack>
                </CardBody>
              </Card>
            </Container>
          </Box>
        );

      case "analytics":
        return (
          <Box className="css-16ktbll" position="relative" opacity={0.4} p={4}>
            <Container maxW="container.xl">
              <Box
                className="chakra-offset-slide"
                bg={previewBg}
                borderRadius="xl"
                p={6}
                boxShadow="xl"
              >
                <VStack spacing={6}>
                  <Heading size="lg" color={textColor}>
                    📊 Analytics Dashboard
                  </Heading>

                  {/* Stats grid */}
                  <HStack spacing={6} w="full" justify="center">
                    <Card bg={cardBg} p={4} borderRadius="xl" minW="150px">
                      <VStack>
                        <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                          1,247
                        </Text>
                        <Text fontSize="sm" color={mutedTextColor}>
                          Words Learned
                        </Text>
                      </VStack>
                    </Card>
                    <Card bg={cardBg} p={4} borderRadius="xl" minW="150px">
                      <VStack>
                        <Text
                          fontSize="2xl"
                          fontWeight="bold"
                          color="green.500"
                        >
                          7
                        </Text>
                        <Text fontSize="sm" color={mutedTextColor}>
                          Day Streak
                        </Text>
                      </VStack>
                    </Card>
                    <Card bg={cardBg} p={4} borderRadius="xl" minW="150px">
                      <VStack>
                        <Text
                          fontSize="2xl"
                          fontWeight="bold"
                          color="purple.500"
                        >
                          89%
                        </Text>
                        <Text fontSize="sm" color={mutedTextColor}>
                          Accuracy
                        </Text>
                      </VStack>
                    </Card>
                  </HStack>

                  {/* Chart placeholder */}
                  <Card
                    bg={cardBg}
                    p={6}
                    w="full"
                    borderRadius="xl"
                    minH="200px"
                  >
                    <VStack spacing={4}>
                      <Text fontWeight="bold" color={textColor}>
                        Learning Progress
                      </Text>
                      <Box
                        w="full"
                        h="150px"
                        bg="gray.100"
                        borderRadius="lg"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="gray.500">📈 Progress Chart</Text>
                      </Box>
                    </VStack>
                  </Card>
                </VStack>
              </Box>
            </Container>
          </Box>
        );

      case "settings":
        return (
          <Box className="css-16ktbll" position="relative" opacity={0.4} p={4}>
            <Container maxW="container.md">
              <Box
                className="chakra-offset-slide"
                bg={previewBg}
                borderRadius="xl"
                p={6}
                boxShadow="xl"
              >
                <VStack spacing={6}>
                  <Heading size="lg" color={textColor}>
                    ⚙️ Goal Settings
                  </Heading>

                  <VStack spacing={4} w="full">
                    <Card bg={cardBg} p={4} w="full" borderRadius="xl">
                      <VStack spacing={3} align="start">
                        <Text fontWeight="bold" color={textColor}>
                          Daily Goal
                        </Text>
                        <HStack w="full" spacing={4}>
                          <Text fontSize="sm" color={mutedTextColor}>
                            15 min
                          </Text>
                          <Box flex={1} bg="gray.200" h="2" borderRadius="full">
                            <Box
                              bg="blue.500"
                              h="2"
                              borderRadius="full"
                              w="60%"
                            />
                          </Box>
                          <Text fontSize="sm" color={mutedTextColor}>
                            60 min
                          </Text>
                        </HStack>
                        <Text fontSize="sm" color="blue.500">
                          Current: 30 minutes/day
                        </Text>
                      </VStack>
                    </Card>

                    <Card bg={cardBg} p={4} w="full" borderRadius="xl">
                      <VStack spacing={3} align="start">
                        <Text fontWeight="bold" color={textColor}>
                          Difficulty Level
                        </Text>
                        <HStack w="full" justify="space-between">
                          <Text fontSize="sm" color={mutedTextColor}>
                            Beginner
                          </Text>
                          <Text
                            fontSize="sm"
                            color="blue.500"
                            fontWeight="bold"
                          >
                            Intermediate ✓
                          </Text>
                          <Text fontSize="sm" color={mutedTextColor}>
                            Advanced
                          </Text>
                        </HStack>
                      </VStack>
                    </Card>

                    <Card bg={cardBg} p={4} w="full" borderRadius="xl">
                      <VStack spacing={3} align="start">
                        <Text fontWeight="bold" color={textColor}>
                          Notifications
                        </Text>
                        <HStack justify="space-between" w="full">
                          <Text fontSize="sm" color={textColor}>
                            Daily Reminders
                          </Text>
                          <Box
                            w="12"
                            h="6"
                            bg="green.500"
                            borderRadius="full"
                          />
                        </HStack>
                      </VStack>
                    </Card>
                  </VStack>
                </VStack>
              </Box>
            </Container>
          </Box>
        );

      case "profile":
        return (
          <Box className="css-16ktbll" position="relative" opacity={0.4} p={4}>
            <Container maxW="container.lg">
              <Box
                className="chakra-offset-slide"
                bg={previewBg}
                borderRadius="xl"
                p={6}
                boxShadow="xl"
              >
                <VStack spacing={6}>
                  <Heading size="lg" color={textColor}>
                    👥 Learning Community
                  </Heading>

                  {/* Leaderboard */}
                  <Card bg={cardBg} p={4} w="full" borderRadius="xl">
                    <VStack spacing={3}>
                      <Text fontWeight="bold" color={textColor}>
                        Weekly Leaderboard
                      </Text>
                      <VStack spacing={2} w="full">
                        {[
                          {
                            name: "Sarah M.",
                            points: "2,847",
                            rank: 1,
                            flag: "🇳🇱",
                          },
                          { name: "You", points: "1,247", rank: 2, flag: "🇺🇸" },
                          {
                            name: "David L.",
                            points: "1,156",
                            rank: 3,
                            flag: "🇫🇷",
                          },
                        ].map((user, index) => (
                          <HStack
                            key={index}
                            w="full"
                            justify="space-between"
                            p={2}
                            bg={index === 1 ? "blue.50" : "transparent"}
                            borderRadius="lg"
                          >
                            <HStack>
                              <Text fontSize="lg">
                                {user.rank === 1
                                  ? "🥇"
                                  : user.rank === 2
                                  ? "🥈"
                                  : "🥉"}
                              </Text>
                              <Text
                                fontWeight={index === 1 ? "bold" : "normal"}
                              >
                                {user.name}
                              </Text>
                              <Text>{user.flag}</Text>
                            </HStack>
                            <Text fontWeight="bold" color="blue.500">
                              {user.points}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </VStack>
                  </Card>

                  {/* Challenges */}
                  <Card bg={cardBg} p={4} w="full" borderRadius="xl">
                    <VStack spacing={3}>
                      <Text fontWeight="bold" color={textColor}>
                        Active Challenges
                      </Text>
                      <HStack spacing={4} w="full">
                        <Card bg="orange.50" p={3} borderRadius="lg" flex={1}>
                          <VStack spacing={1}>
                            <Text fontSize="lg">🔥</Text>
                            <Text fontSize="sm" fontWeight="bold">
                              7-Day Streak
                            </Text>
                            <Text fontSize="xs" color={mutedTextColor}>
                              6/7 days
                            </Text>
                          </VStack>
                        </Card>
                        <Card bg="purple.50" p={3} borderRadius="lg" flex={1}>
                          <VStack spacing={1}>
                            <Text fontSize="lg">🎯</Text>
                            <Text fontSize="sm" fontWeight="bold">
                              100 Words
                            </Text>
                            <Text fontSize="xs" color={mutedTextColor}>
                              87/100
                            </Text>
                          </VStack>
                        </Card>
                      </HStack>
                    </VStack>
                  </Card>
                </VStack>
              </Box>
            </Container>
          </Box>
        );

      default:
        return null;
    }
  }

  const features = [
    {
      id: 1,
      icon: FiPlay,
      title: t("onboarding.tour.features.playMode.title"),
      description: t("onboarding.tour.features.playMode.description"),
      benefits: [
        t("onboarding.tour.features.playMode.benefit1"),
        t("onboarding.tour.features.playMode.benefit2"),
        t("onboarding.tour.features.playMode.benefit3"),
      ],
      color: "blue",
      gradient: "linear(to-r, blue.400, cyan.400)",
      component: () => renderPagePreview("play"),
      containerClass: ".chakra-card",
    },
    {
      id: 2,
      icon: FiCpu,
      title: t("onboarding.tour.features.smartPractice.title"),
      description: t("onboarding.tour.features.smartPractice.description"),
      benefits: [
        t("onboarding.tour.features.smartPractice.benefit1"),
        t("onboarding.tour.features.smartPractice.benefit2"),
        t("onboarding.tour.features.smartPractice.benefit3"),
      ],
      color: "purple",
      gradient: "linear(to-r, purple.400, pink.400)",
      component: () => renderPagePreview("smart-practice"),
      containerClass: ".css-v4s7lz",
    },
    {
      id: 3,
      icon: FiBookOpen,
      title: t("onboarding.tour.features.smartRead.title"),
      description: t("onboarding.tour.features.smartRead.description"),
      benefits: [
        t("onboarding.tour.features.smartRead.benefit1"),
        t("onboarding.tour.features.smartRead.benefit2"),
        t("onboarding.tour.features.smartRead.benefit3"),
      ],
      color: "teal",
      gradient: "linear(to-r, teal.400, blue.400)",
      component: () => renderPagePreview("smart-read"),
      containerClass: ".chakra-container",
    },
    {
      id: 4,
      icon: FiTrendingUp,
      title: t("onboarding.tour.features.analytics.title"),
      description: t("onboarding.tour.features.analytics.description"),
      benefits: [
        t("onboarding.tour.features.analytics.benefit1"),
        t("onboarding.tour.features.analytics.benefit2"),
        t("onboarding.tour.features.analytics.benefit3"),
      ],
      color: "green",
      gradient: "linear(to-r, green.400, teal.400)",
      component: () => renderPagePreview("analytics"),
      containerClass: "div.chakra-offset-slide:nth-child(1)",
    },
    {
      id: 5,
      icon: FiTarget,
      title: t("onboarding.tour.features.goals.title"),
      description: t("onboarding.tour.features.goals.description"),
      benefits: [
        t("onboarding.tour.features.goals.benefit1"),
        t("onboarding.tour.features.goals.benefit2"),
        t("onboarding.tour.features.goals.benefit3"),
      ],
      color: "orange",
      gradient: "linear(to-r, orange.400, yellow.400)",
      component: () => renderPagePreview("settings"),
      containerClass: ".chakra-offset-slide",
    },
    {
      id: 6,
      icon: FiUsers,
      title: t("onboarding.tour.features.community.title"),
      description: t("onboarding.tour.features.community.description"),
      benefits: [
        t("onboarding.tour.features.community.benefit1"),
        t("onboarding.tour.features.community.benefit2"),
        t("onboarding.tour.features.community.benefit3"),
      ],
      color: "cyan",
      gradient: "linear(to-r, cyan.400, blue.400)",
      component: () => renderPagePreview("profile"),
      containerClass: "div.chakra-offset-slide:nth-child(1)",
    },
  ];

  const handleFeatureComplete = (featureId: number) => {
    if (!completedFeatures.includes(featureId)) {
      setCompletedFeatures((prev) => [...prev, featureId]);
    }
  };

  const handleNext = () => {
    if (currentFeature < features.length - 1) {
      setCurrentFeature((prev) => prev + 1);
      handleFeatureComplete(features[currentFeature].id);
    } else {
      handleFeatureComplete(features[currentFeature].id);
      // Show welcome splash screen before completing onboarding
      setShowWelcomeSplash(true);
    }
  };

  const handlePrevious = () => {
    if (currentFeature > 0) {
      setCurrentFeature((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    setShowWelcomeSplash(true);
  };

  const handleCompleteOnboarding = () => {
    onNext({
      completedFeatures: [...completedFeatures, features[currentFeature].id],
    });
  };

  // Handle swipe gestures for mobile
  const handleDragEnd = (_event: any, info: PanInfo) => {
    void _event;
    if (!isMobile) return;

    const threshold = 50;

    if (info.offset.x > threshold && currentFeature > 0) {
      handlePrevious();
    } else if (
      info.offset.x < -threshold &&
      currentFeature < features.length - 1
    ) {
      handleNext();
    }
  };

  const currentFeatureData = features[currentFeature];
  const progressPercentage = ((currentFeature + 1) / features.length) * 100;

  // Enhanced Gamified Welcome Splash Screen
  if (showWelcomeSplash) {
    return (
      <Box minH="100vh" position="relative" overflow="hidden">
        {/* Animated background with particles */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgGradient="linear(45deg, #0F0C29 0%, #302b63 50%, #24243e 100%)"
          _dark={{
            bgGradient: "linear(45deg, #0F0C29 0%, #302b63 50%, #24243e 100%)",
          }}
          _light={{
            bgGradient: "linear(45deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
          }}
        >
          {/* Floating particles */}
          {[...Array(20)].map((_, i) => (
            <MotionBox
              key={i}
              position="absolute"
              w="4px"
              h="4px"
              bg="white"
              borderRadius="full"
              opacity={0.3}
              initial={{
                x: Math.random() * window.innerWidth,
                y: Math.random() * window.innerHeight,
              }}
              animate={{
                y: [-20, 0, -20],
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </Box>

        {/* Main content */}
        <Box
          position="relative"
          zIndex={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minH="100vh"
          p={4}
        >
          <Container maxW="6xl">
            <AnimatePresence>
              <MotionBox
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, ease: "easeOut" }}
              >
                <VStack spacing={12} align="center" textAlign="center">
                  {/* Epic Game Logo */}
                  <MotionBox
                    initial={{ opacity: 0, y: -50, rotate: -10 }}
                    animate={{ opacity: 1, y: 0, rotate: 0 }}
                    transition={{ delay: 0.3, duration: 0.8, type: "spring" }}
                  >
                    <Box
                      w="32"
                      h="32"
                      borderRadius="3xl"
                      bgGradient="linear(45deg, #ff6b6b, #feca57, #48dbfb, #ff9ff3)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      boxShadow="0 20px 40px rgba(0,0,0,0.3)"
                      position="relative"
                      _before={{
                        content: '""',
                        position: "absolute",
                        top: "-2px",
                        left: "-2px",
                        right: "-2px",
                        bottom: "-2px",
                        background:
                          "linear-gradient(45deg, #ff6b6b, #feca57, #48dbfb, #ff9ff3)",
                        borderRadius: "3xl",
                        zIndex: -1,
                        filter: "blur(10px)",
                        opacity: 0.7,
                      }}
                    >
                      <Text fontSize="6xl" fontWeight="black" color="white">
                        H
                      </Text>
                    </Box>
                  </MotionBox>

                  {/* Achievement Unlocked Style Header */}
                  <VStack spacing={6}>
                    <MotionBox
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.6, duration: 0.5, type: "spring" }}
                    >
                      <Box
                        bg="rgba(255, 255, 255, 0.1)"
                        backdropFilter="blur(10px)"
                        borderRadius="full"
                        px={6}
                        py={3}
                        border="2px solid rgba(255, 255, 255, 0.2)"
                        boxShadow="0 8px 32px rgba(0,0,0,0.2)"
                      >
                        <HStack spacing={3}>
                          <Icon as={FaTrophy} color="gold" fontSize="xl" />
                          <Text color="white" fontWeight="bold" fontSize="lg">
                            ACHIEVEMENT UNLOCKED
                          </Text>
                          <Icon as={FaTrophy} color="gold" fontSize="xl" />
                        </HStack>
                      </Box>
                    </MotionBox>

                    <MotionBox
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.9, duration: 0.6 }}
                    >
                      <Heading
                        size="4xl"
                        color="white"
                        fontWeight="black"
                        textShadow="0 4px 20px rgba(0,0,0,0.5)"
                        lineHeight="shorter"
                      >
                        LANGUAGE MASTER
                      </Heading>
                    </MotionBox>

                    <MotionBox
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.1, duration: 0.6 }}
                    >
                      <Text
                        fontSize="2xl"
                        color="rgba(255, 255, 255, 0.9)"
                        fontWeight="medium"
                        textShadow="0 2px 10px rgba(0,0,0,0.3)"
                      >
                        {t("onboarding.tour.welcomeToHanscard")}
                      </Text>
                    </MotionBox>
                  </VStack>

                  {/* Stats Card */}
                  <MotionBox
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.3, duration: 0.6 }}
                  >
                    <Card
                      bg="rgba(255, 255, 255, 0.1)"
                      backdropFilter="blur(15px)"
                      borderRadius="3xl"
                      border="1px solid rgba(255, 255, 255, 0.2)"
                      boxShadow="0 20px 40px rgba(0,0,0,0.2)"
                      overflow="hidden"
                      maxW="2xl"
                    >
                      <CardBody p={8}>
                        <VStack spacing={6}>
                          <Text
                            fontSize="xl"
                            color="white"
                            fontWeight="bold"
                            textAlign="center"
                          >
                            🎯 Your Learning Journey Starts Now!
                          </Text>

                          {/* Progress indicators */}
                          <SimpleGrid columns={3} spacing={6} w="full">
                            <VStack spacing={2}>
                              <Box
                                w="16"
                                h="16"
                                borderRadius="xl"
                                bg="rgba(52, 211, 153, 0.2)"
                                border="2px solid #34d399"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Text fontSize="2xl">📚</Text>
                              </Box>
                              <Text
                                color="white"
                                fontSize="sm"
                                fontWeight="medium"
                              >
                                {selectedWords.length} Words Ready
                              </Text>
                            </VStack>

                            <VStack spacing={2}>
                              <Box
                                w="16"
                                h="16"
                                borderRadius="xl"
                                bg="rgba(59, 130, 246, 0.2)"
                                border="2px solid #3b82f6"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Text fontSize="2xl">🚀</Text>
                              </Box>
                              <Text
                                color="white"
                                fontSize="sm"
                                fontWeight="medium"
                              >
                                Level 1 Unlocked
                              </Text>
                            </VStack>

                            <VStack spacing={2}>
                              <Box
                                w="16"
                                h="16"
                                borderRadius="xl"
                                bg="rgba(168, 85, 247, 0.2)"
                                border="2px solid #a855f7"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Text fontSize="2xl">⚡</Text>
                              </Box>
                              <Text
                                color="white"
                                fontSize="sm"
                                fontWeight="medium"
                              >
                                AI Tutor Active
                              </Text>
                            </VStack>
                          </SimpleGrid>

                          <Text
                            color="rgba(255, 255, 255, 0.8)"
                            textAlign="center"
                            fontSize="lg"
                          >
                            Your personalized learning adventure awaits. Ready
                            to become fluent?
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  </MotionBox>

                  {/* Epic Start Button */}
                  <MotionBox
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.6, duration: 0.6 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      size="xl"
                      py={8}
                      px={16}
                      fontSize="2xl"
                      fontWeight="black"
                      borderRadius="2xl"
                      bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      color="white"
                      _hover={{
                        bg: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                      }}
                      _active={{
                        transform: "translateY(0)",
                      }}
                      rightIcon={<Icon as={FaRocket} fontSize="2xl" />}
                      onClick={handleCompleteOnboarding}
                      isLoading={isLoading}
                      loadingText="Launching..."
                      boxShadow="0 15px 30px rgba(0,0,0,0.2)"
                      transition="all 0.3s ease"
                      textShadow="0 2px 4px rgba(0,0,0,0.3)"
                    >
                      {t("onboarding.tour.letsPlay")}
                    </Button>
                  </MotionBox>

                  {/* Floating achievement badges */}
                  <HStack spacing={4} flexWrap="wrap" justify="center">
                    {[
                      { icon: FaGamepad, label: "Gamer", color: "#ff6b6b" },
                      { icon: FaFire, label: "Motivated", color: "#feca57" },
                      { icon: FaStar, label: "Achiever", color: "#48dbfb" },
                      { icon: FaRocket, label: "Explorer", color: "#ff9ff3" },
                    ].map((badge, index) => (
                      <MotionBox
                        key={badge.label}
                        initial={{ opacity: 0, scale: 0, rotate: -180 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        transition={{
                          delay: 1.8 + index * 0.1,
                          duration: 0.5,
                          type: "spring",
                        }}
                        whileHover={{
                          scale: 1.1,
                          rotate: 5,
                          transition: { duration: 0.2 },
                        }}
                      >
                        <VStack spacing={1} opacity={0.8}>
                          <Box
                            w="12"
                            h="12"
                            borderRadius="full"
                            bg={badge.color}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            boxShadow="0 8px 16px rgba(0,0,0,0.2)"
                          >
                            <Icon as={badge.icon} fontSize="xl" color="white" />
                          </Box>
                          <Text color="white" fontSize="xs" fontWeight="medium">
                            {badge.label}
                          </Text>
                        </VStack>
                      </MotionBox>
                    ))}
                  </HStack>
                </VStack>
              </MotionBox>
            </AnimatePresence>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      minH="100vh"
      bgGradient={bgGradient}
      position="relative"
      overflow="hidden"
    >
      {/* Container for the actual component preview with navigation */}
      <Box position="relative" h={containerHeight} w="100%" overflow="hidden">
        {/* Navigation arrows for desktop */}
        {!isMobile && (
          <>
            <IconButton
              aria-label="Previous feature"
              icon={<FiChevronLeft />}
              position="absolute"
              left={4}
              top="50%"
              transform="translateY(-50%)"
              zIndex={10}
              bg={cardBg}
              color={textColor}
              borderRadius="full"
              size="lg"
              fontSize="2xl"
              boxShadow="xl"
              isDisabled={currentFeature === 0}
              onClick={handlePrevious}
              _hover={{
                transform: "translateY(-50%) scale(1.1)",
                bg: accentColor,
                color: "white",
              }}
              transition="all 0.2s ease"
            />

            <IconButton
              aria-label="Next feature"
              icon={<FiChevronRight />}
              position="absolute"
              right={4}
              top="50%"
              transform="translateY(-50%)"
              zIndex={10}
              bg={cardBg}
              color={textColor}
              borderRadius="full"
              size="lg"
              fontSize="2xl"
              boxShadow="xl"
              isDisabled={currentFeature === features.length - 1}
              onClick={handleNext}
              _hover={{
                transform: "translateY(-50%) scale(1.1)",
                bg: accentColor,
                color: "white",
              }}
              transition="all 0.2s ease"
            />
          </>
        )}

        {/* Render the actual component preview (swipeable on mobile) */}
        <MotionBox
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.4}
          pointerEvents="none"
          transform="scale(0.95)"
          transformOrigin="center"
          drag={isMobile ? "x" : false}
          dragConstraints={{ left: 0, right: 0 }}
          onDragEnd={handleDragEnd}
          dragElastic={0.1}
          whileDrag={isMobile ? { scale: 0.98 } : undefined}
        >
          {currentFeatureData.component()}
        </MotionBox>

        {/* Semi-transparent overlay with feature explanation */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg={overlayBg}
          backdropFilter="blur(2px)"
          zIndex={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={4}
        >
          <Container maxW="4xl">
            <AnimatePresence mode="wait">
              <MotionBox
                key={currentFeature}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                transition={{ duration: 0.5 }}
              >
                <VStack spacing={8} align="center" textAlign="center">
                  {/* Feature Icon and Title */}
                  <VStack spacing={4}>
                    <Box
                      w="20"
                      h="20"
                      borderRadius="3xl"
                      bgGradient={currentFeatureData.gradient}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      boxShadow="2xl"
                      animation="float 3s ease-in-out infinite"
                    >
                      <Icon
                        as={currentFeatureData.icon}
                        fontSize="3xl"
                        color="white"
                      />
                    </Box>

                    <VStack spacing={2}>
                      <Heading size="2xl" color={textColor} fontWeight="bold">
                        {currentFeatureData.title}
                      </Heading>
                      <Text fontSize="xl" color={mutedTextColor} maxW="3xl">
                        {currentFeatureData.description}
                      </Text>
                    </VStack>
                  </VStack>

                  {/* Benefits List */}
                  <Card
                    bg={cardBg}
                    boxShadow="2xl"
                    borderRadius="2xl"
                    overflow="hidden"
                    maxW="2xl"
                  >
                    <CardBody p={8}>
                      <VStack spacing={4} align="stretch">
                        <Heading
                          size="lg"
                          color={accentColor}
                          mb={2}
                          textAlign="center"
                        >
                          {t("onboarding.tour.benefits")}
                        </Heading>
                        {currentFeatureData.benefits.map((benefit, index) => (
                          <MotionBox
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 + 0.5 }}
                          >
                            <HStack align="start" spacing={4}>
                              <Box
                                w="8"
                                h="8"
                                borderRadius="full"
                                bg={successColor}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                flexShrink={0}
                              >
                                <Icon
                                  as={FaCheck}
                                  color="white"
                                  fontSize="sm"
                                />
                              </Box>
                              <Text
                                fontSize="lg"
                                color={textColor}
                                lineHeight="1.6"
                              >
                                {benefit}
                              </Text>
                            </HStack>
                          </MotionBox>
                        ))}
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Progress and Navigation */}
                  <VStack spacing={6} w="full" maxW="2xl">
                    {/* Progress Bar */}
                    <VStack spacing={2} w="full">
                      <HStack justify="space-between" w="full">
                        <Text fontSize="sm" color={mutedTextColor}>
                          Step {currentFeature + 1} of {features.length}
                        </Text>
                        <Text fontSize="sm" color={mutedTextColor}>
                          {progressPercentage.toFixed(0)}% Complete
                        </Text>
                      </HStack>
                      <Progress
                        value={progressPercentage}
                        w="full"
                        h="3"
                        borderRadius="full"
                        bg={progressTrackBg}
                        colorScheme="blue"
                      />
                    </VStack>

                    {/* Navigation Buttons */}
                    <HStack spacing={4} justify="center">
                      <Button
                        variant="outline"
                        colorScheme="gray"
                        onClick={handlePrevious}
                        isDisabled={currentFeature === 0}
                        size="lg"
                        borderRadius="full"
                        px={8}
                      >
                        {t("common.previous")}
                      </Button>

                      <Button
                        variant="ghost"
                        onClick={handleSkip}
                        size="lg"
                        borderRadius="full"
                        px={8}
                        color={mutedTextColor}
                      >
                        {t("onboarding.tour.skipTour")}
                      </Button>

                      <Button
                        colorScheme="blue"
                        onClick={handleNext}
                        size="lg"
                        borderRadius="full"
                        px={8}
                        rightIcon={
                          currentFeature === features.length - 1 ? (
                            <FiSend />
                          ) : undefined
                        }
                        bgGradient={currentFeatureData.gradient}
                        _hover={{
                          bgGradient: currentFeatureData.gradient,
                          transform: "translateY(-2px)",
                          boxShadow: "2xl",
                        }}
                        boxShadow="lg"
                      >
                        {currentFeature === features.length - 1
                          ? t("onboarding.tour.startLearning")
                          : t("onboarding.tour.nextFeature")}
                      </Button>
                    </HStack>

                    {/* Feature Dots */}
                    <HStack spacing={3} justify="center">
                      {features.map((feature, index) => (
                        <Box
                          key={feature.id}
                          w={index === currentFeature ? "12px" : "8px"}
                          h={index === currentFeature ? "12px" : "8px"}
                          borderRadius="full"
                          bg={
                            index === currentFeature
                              ? accentColor
                              : dotInactiveBg
                          }
                          transition="all 0.3s ease"
                          cursor="pointer"
                          onClick={() => setCurrentFeature(index)}
                          _hover={{
                            transform:
                              index !== currentFeature ? "scale(1.2)" : "none",
                          }}
                        />
                      ))}
                    </HStack>

                    {/* Mobile swipe hint */}
                    {isMobile && (
                      <Text
                        fontSize="xs"
                        color={mutedTextColor}
                        textAlign="center"
                      >
                        👈 Swipe left or right to navigate 👉
                      </Text>
                    )}
                  </VStack>
                </VStack>
              </MotionBox>
            </AnimatePresence>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default FeatureTour;
